/* LlmBudgetBanner — shows when the Emergent LLM key has had a recent
 * auth/quota failure. Friendly + actionable: tells user how to top up.
 * - Polls /api/llm-status every 60s.
 * - Also listens for a global `llm-failure` CustomEvent so it can appear
 *   INSTANTLY when a chat/experter/iris call throws a quota error.
 * - Self-hides when healthy. */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, X, RefreshCw, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public helper — call from any error handler to signal LLM failure globally.
// Frontend components should call this instead of showing a raw toast.
export function notifyLlmFailure(reason = '') {
  try {
    window.dispatchEvent(new CustomEvent('llm-failure', { detail: { reason } }));
  } catch {/* SSR / no-op */}
}

export default function LlmBudgetBanner({ inline = false }) {
  const [healthy, setHealthy] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/llm-status`, { withCredentials: true });
      const ok = r.data?.healthy !== false;
      setHealthy(ok);
      if (!ok) setDismissed(false); // resurface if it was previously dismissed
    } catch {/**/}
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, 60000);
    // Listen for immediate failure signals so we don't wait for the poll
    const onFail = () => {
      // Optimistically show — backend will confirm on next poll
      setHealthy(false);
      setDismissed(false);
      // Also trigger a real check so we pick up the persisted state
      setTimeout(check, 800);
    };
    window.addEventListener('llm-failure', onFail);
    return () => {
      clearInterval(id);
      window.removeEventListener('llm-failure', onFail);
    };
  }, [check]);

  const markRecovered = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/llm-status/reset`, {}, { withCredentials: true });
      setHealthy(true);
      setDismissed(false);
    } catch {/**/}
    setBusy(false);
  };

  if (healthy || dismissed) return null;

  const wrap = inline
    ? 'mx-auto max-w-2xl mb-3'
    : 'fixed top-3 left-1/2 -translate-x-1/2 z-[95] w-[92vw] max-w-2xl';

  return (
    <div className={wrap} data-testid="llm-budget-banner">
      <div className="bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 border-2 border-amber-400 rounded-2xl shadow-lg px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold text-amber-900 flex items-center gap-1.5">
            Universal Key-saldot verkar slut 💸
          </div>
          <p className="text-xs text-amber-800/90 leading-snug mt-0.5">
            AI-experter, Farmor, Iris, Tidsfeed-bilder m.fl. behöver saldo för att prata.
            Fyll på i <strong>Profil → Manage plan → Universal Key → Add Balance</strong>.
            <span className="ml-1 opacity-80">Tips: slå på <b>auto top-up</b> så slipper du tänka på det. 🐾</span>
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <a
              href="https://app.emergent.sh/profile"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="llm-banner-open-profile"
              className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-800 hover:bg-amber-900 text-white shadow-sm"
            >
              <Sparkles className="w-3 h-3" /> Öppna Profil → Universal Key
            </a>
            <button
              onClick={markRecovered}
              disabled={busy}
              data-testid="llm-banner-recovered"
              className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/80 hover:bg-white text-amber-800 border border-amber-300"
            >
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
              Jag har fyllt på — kolla igen
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="llm-banner-dismiss"
          className="text-amber-700 hover:text-amber-900 p-1 -mt-1"
          title="Stäng"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
